import { type RefrigerationUnit } from '@/data/schemas';
import type { RefrigerationUnitTechnicalInput } from '@/data/repos/refrigerationUnitsRepo';
import { toOptionalNumber, type RefrigerationUnitFormValues } from './shared';

const normalizeText = (value: string | undefined | null): string => (value ?? '').trim();

export const buildAbatementSystemDefaults = (): Pick<
  RefrigerationUnitFormValues,
  | 'abatementSystemPresent'
  | 'abatementSystemControlMode'
  | 'abatementSystemTrim'
  | 'abatementSystemLimitSwitches'
  | 'abatementSystemType'
  | 'abatementSystemSupply'
  | 'abatementSystemDisposal'
  | 'abatementSystemWaterFlowM3h'
> => ({
  abatementSystemPresent: false,
  abatementSystemControlMode: '',
  abatementSystemTrim: false,
  abatementSystemLimitSwitches: false,
  abatementSystemType: '',
  abatementSystemSupply: '',
  abatementSystemDisposal: '',
  abatementSystemWaterFlowM3h: null,
});

export const buildAbatementSystemValues = (
  refrigerationUnit: RefrigerationUnit
): Pick<
  RefrigerationUnitFormValues,
  | 'abatementSystemPresent'
  | 'abatementSystemControlMode'
  | 'abatementSystemTrim'
  | 'abatementSystemLimitSwitches'
  | 'abatementSystemType'
  | 'abatementSystemSupply'
  | 'abatementSystemDisposal'
  | 'abatementSystemWaterFlowM3h'
> => {
  const abatementSystem = refrigerationUnit.technical?.safety?.abatementSystem;

  return {
    abatementSystemPresent: abatementSystem?.present ?? false,
    abatementSystemControlMode: abatementSystem?.controlMode ?? '',
    abatementSystemTrim: abatementSystem?.trim ?? false,
    abatementSystemLimitSwitches: abatementSystem?.limitSwitches ?? false,
    abatementSystemType: abatementSystem?.type ?? '',
    abatementSystemSupply: abatementSystem?.supply ?? '',
    abatementSystemDisposal: abatementSystem?.disposal ?? '',
    abatementSystemWaterFlowM3h: abatementSystem?.waterFlowM3h ?? null,
  };
};

export const abatementSystemToInput = (
  values: RefrigerationUnitFormValues
): Pick<NonNullable<RefrigerationUnitTechnicalInput['safety']>, 'abatementSystem'> => {
  const rawControlMode = values.abatementSystemControlMode?.trim();
  const controlMode =
    rawControlMode === 'manuale' || rawControlMode === 'automatico' ? rawControlMode : undefined;
  const type = normalizeText(values.abatementSystemType);
  const supply = normalizeText(values.abatementSystemSupply);
  const disposal = normalizeText(values.abatementSystemDisposal);
  const waterFlowM3h = toOptionalNumber(values.abatementSystemWaterFlowM3h ?? undefined);

  return {
    abatementSystem: values.abatementSystemPresent
      ? {
          present: true,
          controlMode,
          trim: values.abatementSystemTrim,
          limitSwitches: values.abatementSystemLimitSwitches,
          type,
          supply,
          disposal,
          waterFlowM3h,
        }
      : { present: false },
  };
};
